import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import DosenTooltip from '../../../../components/ui/DosenTooltip';

// The People page on a phone (below MOBILE_APP_MEDIA): every table row becomes
// a card, its columns label/value pairs. Desktop never renders any of this.

export const MOBILE_CARD_SX = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '10px',
  p: '14px',
  bgcolor: '#1C1C1C',
  border: '1px solid #2A2A2A',
  borderRadius: '8px',
  minWidth: 0
};

const LABEL_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: '#868686',
  lineHeight: 1.2
};

/** A card's figures, two to a row. */
export const MobileStats: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gap: '12px',
      pt: '10px',
      borderTop: '1px solid #262626'
    }}
  >
    {children}
  </Box>
);

/** One column of the table as a label over its value. `wide` takes the whole row. */
export const MobileStat: React.FC<{ label: string; wide?: boolean; children: React.ReactNode }> = ({
  label,
  wide = false,
  children
}) => (
  <Box
    sx={{
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '5px',
      ...(wide ? { gridColumn: '1 / -1' } : null)
    }}
  >
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Box sx={{ minWidth: 0, maxWidth: '100%', display: 'flex', alignItems: 'center', minHeight: 20 }}>{children}</Box>
  </Box>
);

/** The value of a figure that opens nothing: plain text, read from the left. */
export const mobileValueSx = (color = '#FDFDFD') => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  color,
  lineHeight: 1.35,
  overflowWrap: 'anywhere' as const
});

const pillSx = {
  bgcolor: '#2A2A2A',
  borderRadius: '20px',
  px: '10px',
  py: '4px',
  maxWidth: '100%',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.35,
  color: '#FDFDFD',
  // A long unbroken value wraps rather than push the page sideways.
  overflowWrap: 'anywhere' as const
};

/** A tag at the top of a member's card; a tap shows their audience attributes. */
export const MobileAttributesButton: React.FC<{ memberName: string; attributes: string[] }> = ({
  memberName,
  attributes
}) => (
  <DosenTooltip
    tapOnPhone
    placement="bottom-end"
    title={
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {attributes.map((attr) => (
          <Box key={attr} sx={pillSx}>
            {attr}
          </Box>
        ))}
      </Box>
    }
  >
    <Box
      component="button"
      type="button"
      aria-label={`${memberName}'s audience attributes`}
      data-testid="member-attributes"
      sx={{
        flexShrink: 0,
        width: 36,
        height: 36,
        p: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #2A2A2A',
        borderRadius: '4px',
        bgcolor: 'transparent',
        color: '#B6B6B6',
        cursor: 'pointer'
      }}
    >
      <LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />
    </Box>
  </DosenTooltip>
);

/** One of the page's actions: an icon over a short label, so they all fit on one row. */
export const MobileActionButton: React.FC<{
  /** The whole name, for screen readers and the journeys. */
  name: string;
  label: string;
  Icon: SvgIconComponent;
  /** Set only on a toggle, so it reads as pressed or not. */
  active?: boolean;
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ name, label, Icon, active, primary = false, disabled = false, onClick }) => (
  <Box
    component="button"
    type="button"
    aria-label={name}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    sx={{
      flex: '0 1 84px',
      minWidth: 0,
      minHeight: 56,
      p: '6px 2px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '3px',
      border: `1px solid ${primary || active ? 'rgba(247, 148, 32, 0.45)' : '#2A2A2A'}`,
      borderRadius: '8px',
      bgcolor: primary || active ? 'rgba(247, 148, 32, 0.12)' : '#1C1C1C',
      color: primary || active ? '#F79420' : '#FDFDFD',
      fontFamily: 'Inter, sans-serif',
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.2,
      cursor: 'pointer',
      '&:disabled': { opacity: 0.5, cursor: 'default' }
    }}
  >
    <Icon sx={{ fontSize: 20 }} />
    {label}
  </Box>
);

/**
 * A member's attributes as pills that wrap. The first two show; "+N" is a tap
 * that shows the rest (on desktop they sit in a hover tooltip).
 */
export const MobileAttributePills: React.FC<{ attributes: string[]; max?: number }> = ({ attributes, max = 2 }) => {
  const [open, setOpen] = useState(false);
  const shown = open ? attributes : attributes.slice(0, max);
  const rest = attributes.length - shown.length;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minWidth: 0 }}>
      {shown.map((attr) => (
        <Box key={attr} sx={pillSx}>
          {attr}
        </Box>
      ))}
      {rest > 0 && (
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Show ${rest} more`}
          sx={{ ...pillSx, border: 'none', cursor: 'pointer', color: '#F79420', fontWeight: 600 }}
        >
          +{rest}
        </Box>
      )}
    </Box>
  );
};
